import React, { useState } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Chip, 
  List, 
  ListItem, 
  ListItemText, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Card, 
  CardContent, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Alert, 
  CircularProgress,
  DialogContentText,
  Avatar,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Badge,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  ShoppingCart,
  AttachMoney,
  Speed,
  CheckCircle,
  Warning,
  Error,
  Info,
  Visibility,
  Refresh,
  Analytics,
  Timeline,
  BarChart,
  PieChart,
  ShowChart,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Engineering as EngineeringIcon,
  Verified as VerifiedIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  ProductionQuantityLimits as ProductionIcon,
  SupervisorAccount,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';
import { useAuth } from '../../context/AuthContext';
import { useGetDashboardStatsQuery, useGetRecentOrdersQuery, useGetProductionDetailsQuery } from '../../api/dashboardApi';
import { Order } from '../../types/index';
import { getRoleName } from '../../utils/roleUtils';

import { RecentOrder, LowStockMaterial, DashboardStats, ProductionDetails } from '../../types/dashboard';

export const ManagerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { permissions, hasRole } = useRbac();
  const statsQuery = useGetDashboardStatsQuery();
  const recentOrdersQuery = useGetRecentOrdersQuery();
  const productionDetailsQuery = useGetProductionDetailsQuery();

  const stats = statsQuery.data as DashboardStats | undefined;
  const statsLoading = statsQuery.isLoading;
  const statsError = statsQuery.error;

  const recentOrders = recentOrdersQuery.data as RecentOrder[] | undefined;
  const ordersLoading = recentOrdersQuery.isLoading;
  const ordersError = recentOrdersQuery.error;

  const productionDetails = productionDetailsQuery.data as ProductionDetails | undefined;
  const productionLoading = productionDetailsQuery.isLoading;
  const productionError = productionDetailsQuery.error;

  const [openDialog, setOpenDialog] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleToggleDialog = () => {
    setOpenDialog(!openDialog);
  };

  const renderStatsCards = () => {
    if (!stats || statsLoading) return null;

    const managerStatCards = [
      {
        title: 'Total Orders',
        value: stats.totalOrders,
        icon: <ShoppingCart />,
        color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        trend: '+12%',
        trendUp: true,
      },
      {
        title: 'Production Efficiency',
        value: `${stats.productionEfficiency}%`,
        icon: <Speed />,
        color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        trend: '+5.2%',
        trendUp: true,
      },
      {
        title: 'Quality Pass Rate',
        value: `${stats.qualityPassRate}%`,
        icon: <CheckCircle />,
        color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        trend: '+2.1%',
        trendUp: true,
      },
      {
        title: 'Team Performance',
        value: '94%',
        icon: <PeopleIcon />,
        color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        trend: '+2.5%',
        trendUp: true,
      },
    ];

    return (
      <Grid container spacing={3}>
        {managerStatCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar
                    sx={{
                      background: card.color,
                      width: 48,
                      height: 48,
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {card.trendUp ? (
                      <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
                    ) : (
                      <TrendingDown sx={{ color: 'error.main', fontSize: 20 }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 0.5,
                        color: card.trendUp ? 'success.main' : 'error.main',
                        fontWeight: 600,
                      }}
                    >
                      {card.trend}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    background: card.color,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderRecentOrders = () => {
    if (!recentOrders || recentOrders.length === 0 || ordersLoading) return null;

    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mr: 2,
              }}
            >
              <ShoppingCart />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Orders
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Order #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentOrders.slice(0, 5).map((order: RecentOrder) => (
                <TableRow key={order.id} sx={{ '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.04)' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {order.orderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.customerName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${order.totalAmount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      sx={{
                        background: order.status === 'completed' 
                          ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
                          : order.status === 'pending'
                          ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderLowStockMaterials = () => {
    if (!stats?.lowStockMaterials || stats.lowStockMaterials.length === 0 || statsLoading) return null;

    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                mr: 2,
              }}
            >
              <Warning />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Low Stock Materials
            </Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current Stock</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Threshold</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.lowStockMaterials.map((material: LowStockMaterial) => (
                <TableRow key={material.id} sx={{ '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.04)' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {material.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {material.currentStock}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {material.threshold}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label="Low Stock"
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderProductionDetails = () => {
    if (!productionDetails || productionLoading) return null;

    return (
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                mr: 2,
              }}
            >
              <Speed />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Production Details
            </Typography>
          </Box>
          <Typography variant="body1">
            Current production efficiency: {productionDetails.efficiency}%
          </Typography>
          <Typography variant="body1">
            Queue Size: {productionDetails.queue.length}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderError = () => {
    const errors = [statsError, ordersError, productionError].filter(Boolean);
    if (!errors.length) return null;
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Some dashboard data could not be loaded. Showing available information below.
      </Alert>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {renderError()}
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SupervisorAccount sx={{ fontSize: 40, mr: 2, color: '#10b981' }} />
                <Box>
                  <Typography variant="h4" component="h1">
                    Welcome, {user?.name}!
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Manager Portal • {getRoleName(user?.role as any)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {renderStatsCards()}

        <Grid item xs={12}>
          {renderRecentOrders()}
        </Grid>

        <Grid item xs={12} md={6}>
          {renderLowStockMaterials()}
        </Grid>

        <Grid item xs={12} md={6}>
          {renderProductionDetails()}
        </Grid>
      </Grid>
    </Box>
  );
}; 